import {
  Component,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
  NgZone,
  Renderer2,
  DOCUMENT,
  Inject,
  ChangeDetectionStrategy,
  ViewChild,
  ElementRef,
} from '@angular/core';

import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MediaService } from '../../../services/media/media.service';
import { FileItemDTO } from '../../../dto/file-manager/file-item.dto';
import { environment } from '../../../environment/environment';
import { AuthService } from '../../../services/auth/auth.service';
import {
  isAllowedUploadFile,
  resolveUploadMimeTypeFromFile,
} from '../../../utils/upload-mime';

@Component({
  selector: 'app-duna-documents',
  standalone: true,
  imports: [FormsModule, MatProgressBarModule],
  templateUrl: './duna-documents.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./duna-documents-custom.scss'],
})
export class DunaDocumentsComponent implements OnInit, OnDestroy {
  public isLoading = false;
  public errorMessage: string | null = null;
  public currentFiles: FileItemDTO[] = [];
  public currentFolders: FileItemDTO[] = [];
  public currentPath: string = '/';
  public currentScope: { type: string; id?: string | number } = {
    type: 'organization',
  };
  public folderHierarchy: Array<{ name: string; obfuscatedName: string }> = [];
  public helpExpanded = false;

  // Upload progress tracking
  public uploadProgress = 0;
  public isUploading = false;
  public uploadStatus = '';
  public Math = Math; // Expose Math to template

  // Loading state management
  private loadingTimeout: any = null;

  // Folder creation properties
  public showCreateFolderDialog = false;
  public newFolderName = '';
  public isCreatingFolder = false;
  public folderNameInvalidChars: string[] = [];

  // File info modal properties
  public showFileInfoDialog = false;
  public selectedFileInfo: FileItemDTO | null = null;
  public editingDisplayName = false;

  // Move file properties
  public showMoveFileDialog = false;
  /** One or more files to move (single-file flow uses a one-item array). */
  public filesToMove: FileItemDTO[] = [];
  public availableFolders: FileItemDTO[] = [];

  // Multi-select
  public selectedFileObfuscatedNames = new Set<string>();

  // Bulk delete confirmation
  public showDeleteFilesDialog = false;
  public filesPendingDelete: FileItemDTO[] = [];
  public deleteConfirmPhrase = '';
  public readonly deleteConfirmWord = 'SLET';

  // Virtual folder structure
  public virtualFolders = [
    {
      name: 'Begivenheder',
      path: '/Events',
      icon: 'event',
      color: '#4CAF50',
      disabled: true,
    },
    {
      name: 'Lokationer',
      path: '/Locations',
      icon: 'location_on',
      color: '#2196F3',
      disabled: true,
    },
    {
      name: 'Organisation',
      path: '/Organisation',
      icon: 'business',
      color: '#FF9800',
    },
    { name: 'Globalt', path: '/Globalt', icon: 'public', color: '#9C27B0' },
    { name: 'Papirkurv', path: '/Papirkurv', icon: 'delete', color: '#F44336' },
  ];

  // Event-specific folder structure for events
  public eventFolders = [
    {
      name: 'Deltager',
      path: '/Events/Deltager',
      icon: 'people',
      color: '#4CAF50',
      role: 'participant',
    },
    {
      name: 'Instruktør',
      path: '/Events/Instruktør',
      icon: 'school',
      color: '#FF9800',
      role: 'instructor',
    },
  ];

  // Properties for file sharing functionality
  public showShareFileDialog = false;
  public availableShareFiles: FileItemDTO[] = [];
  public fileToShare: FileItemDTO | null = null;
  public shareTargetRole: string = 'participant';
  public shareRoleLocked = false;
  public shareIsBrowsing = false;
  public shareBrowsePath: Array<{
    name: string;
    obfuscatedName: string;
    scopeType: string;
  }> = [];

  // Properties for enhanced file browser
  public fileSearchQuery: string = '';
  public filteredShareFiles: FileItemDTO[] = [];
  public selectedFileCategory: 'all' | 'organization' | 'global' = 'all';
  public expandedCategories: { organization: boolean; global: boolean } = {
    organization: true,
    global: true,
  };

  // URL-based context properties
  public detectedEventUuid: string | null = null;
  public detectedEventId: string | null = null; // Changed to string for UUID
  public isInEventContext = false;
  /** Which event role folder is selected (Deltager/Instruktør); null when at event root */
  public selectedEventRole: 'participant' | 'instructor' | null = null;
  public uploadTargetRole: 'participant' | 'instructor' | null = null;
  public readonly eventRolePanels: Array<{
    role: 'participant' | 'instructor';
    title: string;
    subtitle: string;
    icon: string;
  }> = [
    {
      role: 'participant',
      title: 'Til deltagere',
      subtitle: 'Synlige for tilmeldte deltagere — ikke venteliste',
      icon: 'people',
    },
    {
      role: 'instructor',
      title: 'Til instruktører',
      subtitle: 'Kun synlige for instruktører og dem der kan redigere begivenheden',
      icon: 'school',
    },
  ];

  @ViewChild('fileInput') fileInput?: ElementRef<HTMLInputElement>;

  // Track if current folder is shared (for protecting files inside)
  public currentFolderIsShared = false;

  // Polling for processing files
  private statusPollingIntervals: Map<string, any> = new Map();
  private readonly POLL_INTERVAL = 10000; // 10 seconds

  // Upload progress container rendered at body level
  private uploadProgressElement: HTMLElement | null = null;

  /** Ignores stale listFiles responses when loadFolder is invoked repeatedly */
  private loadFolderGeneration = 0;

  constructor(
    private mediaService: MediaService,
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone,
    private authService: AuthService,
    private router: Router,
    private renderer: Renderer2,
    @Inject(DOCUMENT) private document: Document
  ) {
  }

  ngOnInit(): void {
    this.detectUrlContext();
    this.loadInitialView();
  }

  /**
   * Detect event context from URL
   */
  private detectUrlContext(): void {
    const currentUrl = this.router.url;

    // Check if we're in an event edit context (/edit-event/{uuid})
    const eventMatch = currentUrl.match(/\/edit-event\/([a-fA-F0-9-]{36})/);
    if (eventMatch && eventMatch[1]) {
      this.detectedEventUuid = eventMatch[1];
      this.isInEventContext = true;

      // Validate UUID format
      const uuidPattern = /^[a-fA-F0-9-]{36}$/;
      if (uuidPattern.test(this.detectedEventUuid)) {
        this.detectedEventId = this.detectedEventUuid;

        // Set the current scope to event
        this.currentScope = {
          type: 'event',
          id: this.detectedEventId,
        };

      } else {
        console.error(
          '[DunaDocuments] Invalid UUID format:',
          this.detectedEventUuid
        );
        this.isInEventContext = false;
        this.detectedEventUuid = null;
        this.detectedEventId = null;
        this.currentScope = { type: 'organization' };
      }
    } else {
      this.isInEventContext = false;
      this.detectedEventUuid = null;
      this.detectedEventId = null;
      this.currentScope = { type: 'organization' };
    }
  }

  /**
   * Get appropriate scope ID based on scope type
   * For events: return UUID string, for others: return number
   */
  private getScopeId(
    scopeType: string,
    scopeId: string | number | undefined
  ): string | number | undefined {
    if (scopeType === 'event') {
      // For events, always use UUID string
      if (typeof scopeId === 'string' && scopeId.length === 36) {
        return scopeId;
      } else {
        console.error(
          '[DunaDocuments] Invalid event scope ID:',
          scopeId,
          'Expected UUID string'
        );
        return undefined;
      }
    } else {
      // For organization, location, user - use numeric ID
      return typeof scopeId === 'number' ? scopeId : undefined;
    }
  }

  /**
   * Load initial view - shows virtual folders or event folders
   */
  async loadInitialView(): Promise<void> {
    // Start with root view - user will click on folders to navigate
    this.currentPath = '/';
    this.currentFiles = [];
    this.folderHierarchy = []; // Clear any existing hierarchy

    if (this.isInEventContext) {
      // Automatically load the event's root folder
      await this.loadFolder('/');
    } else {
    }
  }

  /**
   * Handle file input change
   */
  onFileInputChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      if (this.isUploading) {
        this.showError('Upload er allerede i gang');
        input.value = '';
        return;
      }
      // Validate files before upload
      const validationResult = this.validateFiles(input.files);
      if (validationResult.isValid) {
        this.uploadFilesToCurrentFolder(input.files);
      } else {
        this.showError(validationResult.errorMessage);
      }
      input.value = '';
    }
  }

  /** API returns { message, errors?: string[] } on /upload/init validation failures */
  private formatUploadInitError(error: any): string {
    const body = error?.error;
    if (body?.errors && Array.isArray(body.errors) && body.errors.length > 0) {
      return body.errors.map((e: unknown) => String(e)).join('; ');
    }
    if (typeof body?.message === 'string') {
      return body.message;
    }
    return error?.message ?? 'Ukendt fejl';
  }

  /**
   * Validate files before upload
   */
  private validateFiles(files: FileList): {
    isValid: boolean;
    errorMessage: string;
  } {
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const mime = resolveUploadMimeTypeFromFile(file);

      if (!isAllowedUploadFile(file)) {
        return {
          isValid: false,
          errorMessage: `Filtype ikke tilladt: ${file.name} (browser MIME: "${
            file.type || ''
          }", afledt: "${mime}"). Tilladte typer: Billeder, videoer, lyd, dokumenter og OpenDocument.`,
        };
      }

      const maxFileBytes = 1024 * 1024 * 1024; // 1 GB
      const maxVideoBytes = 10 * 1024 * 1024 * 1024; // 10 GB

      if (mime.startsWith('video/') && file.size > maxVideoBytes) {
        return {
          isValid: false,
          errorMessage: `Video for stor: ${file.name} (${this.formatFileSize(
            file.size
          )}). Maksimal størrelse: 10 GB.`,
        };
      }

      if (!mime.startsWith('video/') && file.size > maxFileBytes) {
        return {
          isValid: false,
          errorMessage: `Fil for stor: ${file.name} (${this.formatFileSize(
            file.size
          )}). Maksimal størrelse: 1 GB.`,
        };
      }
    }

    return { isValid: true, errorMessage: '' };
  }

  // ==================== File Manager Events ====================

  /**
   * Delete file (opens strong confirmation dialog)
   */
  deleteFile(obfuscatedName: string): void {
    const file = this.currentFiles.find(
      (f) => f.obfuscated_name === obfuscatedName
    );
    if (!file) {
      return;
    }
    this.openDeleteFilesDialog([file]);
  }

  isInTrashPath(): boolean {
    return (
      this.currentPath.startsWith('/Papirkurv') ||
      this.currentPath.startsWith('/Trash')
    );
  }

  canDeleteFileInCurrentScope(file: FileItemDTO): boolean {
    if (this.isInTrashPath()) {
      return false;
    }
    if (!this.canModifyFile(file)) {
      return false;
    }
    if (
      this.currentPath.startsWith('/Globalt') ||
      this.currentPath.startsWith('/Global')
    ) {
      return this.isSuperadmin();
    }
    return true;
  }

  isFileSelectable(file: FileItemDTO): boolean {
    return this.canDeleteFileInCurrentScope(file);
  }

  get selectableFiles(): FileItemDTO[] {
    return this.currentFiles.filter((f) => this.isFileSelectable(f));
  }

  get selectedFilesCount(): number {
    return this.selectedFileObfuscatedNames.size;
  }

  getSelectedFiles(): FileItemDTO[] {
    return this.currentFiles.filter((f) =>
      this.selectedFileObfuscatedNames.has(f.obfuscated_name)
    );
  }

  isFileSelected(file: FileItemDTO): boolean {
    return this.selectedFileObfuscatedNames.has(file.obfuscated_name);
  }

  toggleFileSelection(file: FileItemDTO, event?: Event): void {
    event?.stopPropagation();
    if (!this.isFileSelectable(file)) {
      return;
    }
    const key = file.obfuscated_name;
    if (this.selectedFileObfuscatedNames.has(key)) {
      this.selectedFileObfuscatedNames.delete(key);
    } else {
      this.selectedFileObfuscatedNames.add(key);
    }
  }

  get allSelectableFilesSelected(): boolean {
    const selectable = this.selectableFiles;
    return (
      selectable.length > 0 &&
      selectable.every((f) =>
        this.selectedFileObfuscatedNames.has(f.obfuscated_name)
      )
    );
  }

  toggleSelectAllFiles(event?: Event): void {
    event?.stopPropagation();
    const selectable = this.selectableFiles;
    if (selectable.length === 0) {
      return;
    }
    if (this.allSelectableFilesSelected) {
      selectable.forEach((f) =>
        this.selectedFileObfuscatedNames.delete(f.obfuscated_name)
      );
    } else {
      selectable.forEach((f) =>
        this.selectedFileObfuscatedNames.add(f.obfuscated_name)
      );
    }
  }

  clearFileSelection(): void {
    this.selectedFileObfuscatedNames.clear();
  }

  canBulkMove(): boolean {
    if (!this.canUpload() || this.isInTrashPath()) {
      return false;
    }
    const selected = this.getSelectedFiles();
    return (
      selected.length > 0 &&
      selected.every((f) => this.canModifyFile(f) && !this.isSharedFile(f))
    );
  }

  canBulkDelete(): boolean {
    const selected = this.getSelectedFiles();
    return (
      selected.length > 0 &&
      selected.every((f) => this.canDeleteFileInCurrentScope(f))
    );
  }

  openDeleteFilesDialog(files: FileItemDTO[]): void {
    this.filesPendingDelete = files;
    this.deleteConfirmPhrase = '';
    this.showDeleteFilesDialog = true;
  }

  openDeleteConfirmationForSelection(): void {
    const files = this.getSelectedFiles().filter((f) =>
      this.canDeleteFileInCurrentScope(f)
    );
    if (files.length === 0) {
      return;
    }
    this.openDeleteFilesDialog(files);
  }

  hideDeleteFilesDialog(): void {
    this.showDeleteFilesDialog = false;
    this.filesPendingDelete = [];
    this.deleteConfirmPhrase = '';
  }

  get deleteConfirmationValid(): boolean {
    return (
      this.deleteConfirmPhrase.trim().toUpperCase() === this.deleteConfirmWord
    );
  }

  async confirmDeleteFiles(): Promise<void> {
    if (!this.deleteConfirmationValid || this.filesPendingDelete.length === 0) {
      return;
    }

    const toDelete = [...this.filesPendingDelete];
    this.hideDeleteFilesDialog();

    try {
      this.setLoading(true);
      let failed = 0;
      for (const file of toDelete) {
        try {
          await this.mediaService.deleteFile(file.obfuscated_name);
        } catch {
          failed++;
        }
      }
      this.clearFileSelection();
      if (failed === 0) {
        this.showSuccess(
          toDelete.length === 1
            ? 'Fil flyttet til papirkurv'
            : `${toDelete.length} filer flyttet til papirkurv`
        );
      } else {
        this.showError(
          `${failed} af ${toDelete.length} filer kunne ikke slettes`
        );
      }
      await this.loadFolder(this.currentPath);
    } catch (error: any) {
      this.showError('Fejl ved sletning: ' + error.message);
    } finally {
      this.setLoading(false);
    }
  }

  /**
   * File opened (double-click or enter)
   */
  async onFileOpen(file: any): Promise<void> {

    if (file.type === 'dir') {
      // Navigate to folder
      this.currentPath = file.path;
      return;
    }

    // Preview file
    await this.previewFile(file);
  }

  /**
   * Upload success
   */
  onUploadSuccess(file: any): void {
    this.showSuccess('Fil uploaded: ' + file.name);
    this.refreshFileManager();
  }

  /**
   * Upload error
   */
  onUploadError(error: any): void {
    console.error('[DunaDocuments] Upload error:', error);
    this.showError('Upload fejlede: ' + error.message);
  }

  /**
   * Delete success
   */
  onDeleteSuccess(paths: string[]): void {
    this.showSuccess(`${paths.length} fil(er) flyttet til papirkurv`);
    this.refreshFileManager();
  }

  /**
   * Error handler
   */
  onError(error: any): void {
    console.error('[DunaDocuments] Error:', error);
    this.showError('Fejl: ' + error.message);
  }

  // ==================== Custom Actions ====================

  /**
   * Restore file from trash
   */
  async restoreFromTrash(obfuscatedName: string): Promise<void> {
    try {
      this.setLoading(true);
      await this.mediaService.restoreFile(obfuscatedName);
      this.showSuccess('Fil gendannet fra papirkurv');
      this.refreshFileManager();
    } catch (error: any) {
      this.showError('Fejl ved gendannelse: ' + error.message);
    } finally {
      this.setLoading(false);
    }
  }

  /**
   * Attach file to event
   */
  async attachToEvent(obfuscatedName: string, eventId: number): Promise<void> {
    try {
      this.setLoading(true);
      await this.mediaService.attachToEvent(obfuscatedName, eventId);
      this.showSuccess('Fil tilknyttet event');
    } catch (error: any) {
      this.showError('Fejl ved tilknytning: ' + error.message);
    } finally {
      this.setLoading(false);
    }
  }

  /**
   * Preview file using the new media preview route
   */
  async previewFile(file: FileItemDTO): Promise<void> {
    try {
      // Check if file is ready for viewing
      if (!this.isFileReady(file)) {
        const statusLabel = this.getStatusLabel(file.status);
        this.showError(
          `Filen er ikke klar til visning. Status: ${statusLabel}`
        );
        return;
      }

      // Navigate to the media preview route with the obfuscated name
      const mediaUrl = `/media/${file.obfuscated_name}`;
      window.open(mediaUrl, '_blank');
    } catch (error: any) {
      this.showError('Forhåndsvisning fejlede: ' + error.message);
    }
  }

  /**
   * Download file using MediaService (with decryption)
   */
  async downloadFile(obfuscatedName: string, filename: string): Promise<void> {
    try {
      // Find the file in current files to check status
      const file = this.currentFiles.find(
        (f) => f.obfuscated_name === obfuscatedName
      );
      if (file && !this.isFileReady(file)) {
        const statusLabel = this.getStatusLabel(file.status);
        this.showError(
          `Filen er ikke klar til download. Status: ${statusLabel}`
        );
        return;
      }

      this.setLoading(true);

      // Use MediaService for encrypted download
      const blob = await this.mediaService.downloadFile(obfuscatedName);

      this.downloadFileByUrl(URL.createObjectURL(blob), filename);
      this.showSuccess('Fil downloaded');
    } catch (error: any) {
      this.showError('Download fejlede: ' + error.message);
    } finally {
      this.setLoading(false);
    }
  }

  /**
   * Change folder/scope
   */
  async changeFolder(path: string): Promise<void> {
    // Check if folder is disabled
    const folder = this.virtualFolders.find((f) => f.path === path);
    if (folder?.disabled) {
      this.showError(
        'Gå direkte til begivenheden eller lokationen for at uploade filer'
      );
      return;
    }

    this.currentPath = path;

    // Check if this is a root virtual folder change (not a subfolder navigation)
    const isRootVirtualFolder = this.virtualFolders.some(
      (vf) => vf.path === path
    );

    if (isRootVirtualFolder) {
      // Reset folder hierarchy when switching between root virtual folders
      this.folderHierarchy = [];
    }

    // Parse scope from path
    if (path.startsWith('/Events/')) {
      this.currentScope = { type: 'event', id: this.extractIdFromPath(path) };
    } else if (path.startsWith('/Locations/')) {
      this.currentScope = {
        type: 'location',
        id: this.extractIdFromPath(path),
      };
    } else if (path.startsWith('/Organisation/')) {
      this.currentScope = {
        type: 'organization',
        id: this.extractIdFromPath(path),
      };
    } else if (path.startsWith('/Globalt') || path.startsWith('/Global')) {
      this.currentScope = { type: 'global' };
    } else if (path.startsWith('/Papirkurv') || path.startsWith('/Trash')) {
      this.currentScope = { type: 'trash' };
    } else if (path === '/Organisation') {
      // This is the Organisation root folder
      this.currentScope = { type: 'organization' };
    } else {
      // If we're navigating to a subfolder without explicit scope, keep the current scope
      // This handles clicking on folders within Organizations
      if (this.currentScope.type === 'organization') {
        // Stay in organization scope for subfolders
        this.currentScope = this.currentScope;
      }
    }

    await this.loadFolder(path);
  }

  /**
   * Load folder contents using your backend
   */
  async loadFolder(path: string): Promise<void> {
    const generation = ++this.loadFolderGeneration;
    try {
      this.setLoading(true, 15000); // Shorter timeout for folder loading
      this.currentPath = path;
      this.clearFileSelection();

      // Track selected event role for Deltager/Instruktør button state
      if (this.isInEventContext) {
        if (path === '/') {
          this.selectedEventRole = null;
        } else if (path === '/Events/Deltager') {
          this.selectedEventRole = 'participant';
        } else if (path === '/Events/Instruktør') {
          this.selectedEventRole = 'instructor';
        }
        // When path is obfuscated (subfolder), keep existing selectedEventRole
      }

      // Parse scope from path
      let scopeType: string | undefined;
      let scopeId: string | number | undefined;
      let includeTrash = false;

      // Handle scope detection based on context and path
      if (this.isInEventContext) {
        // When in event context, always use event scope with detected UUID
        scopeType = 'event';
        scopeId = this.detectedEventId || undefined;
      } else if (path.startsWith('/Papirkurv') || path.startsWith('/Trash')) {
        includeTrash = true;
      } else if (path.startsWith('/Events')) {
        scopeType = 'event';
        scopeId = this.extractIdFromPath(path);
      } else if (path.startsWith('/Locations')) {
        scopeType = 'location';
        scopeId = this.extractIdFromPath(path);
      } else if (path.startsWith('/Organisation')) {
        scopeType = 'organization';
        scopeId = this.extractIdFromPath(path);
        // If no specific organization ID in path, use current user's organization ID
        if (!scopeId) {
          const currentUser = this.authService.getCurrentUser();
          scopeId = currentUser?.user_info?.organizations_id;
        }
      } else if (path.startsWith('/Globalt') || path.startsWith('/Global')) {
        scopeType = 'global';
      }

      // Use MediaService to list files (uses Angular HTTP with your interceptor!)
      const requestData: any = {
        scope_type: scopeType,
        include_trash: includeTrash,
        parent_folder:
          path === '/' ||
          path.startsWith('/Organisation') ||
          path.startsWith('/Globalt') ||
          path.startsWith('/Events') ||
          path.startsWith('/Locations')
            ? undefined
            : path, // Only use path for subfolders
      };

      // Only add scope_id if we have a valid one
      const validScopeId: string | number | undefined = scopeType
        ? this.getScopeId(scopeType, scopeId)
        : undefined;
      if (
        validScopeId !== undefined &&
        (typeof validScopeId === 'string' || typeof validScopeId === 'number')
      ) {
        requestData.scope_id = validScopeId;
      }

      const response: any = await this.mediaService.listFiles(requestData);

      if (generation !== this.loadFolderGeneration) {
        return;
      }

      const listedFiles = this.extractListedFiles(response);

      // Separate files and folders
      this.currentFiles = listedFiles.filter((file) => !file.is_folder);
      this.currentFolders = listedFiles.filter((file) => file.is_folder);

      // Start polling for files that are processing
      this.startPollingForProcessingFiles();

      // Update current folder shared status if we're inside a folder
      // (navigateToFolder sets this, but we also need to handle direct path navigation)
      if (this.currentPath && !this.currentPath.startsWith('/')) {
        // We're inside a folder - check if we can find it in the response
        // If not found, keep the current value (set by navigateToFolder)
        const currentFolder = this.currentFolders.find(
          (f) => f.obfuscated_name === this.currentPath
        );
        if (currentFolder) {
          this.currentFolderIsShared = this.isSharedFolder(currentFolder);
        } else {
          // Check all items in response (might include the folder we're in)
          const allItems = listedFiles;
          const folderInResponse = allItems.find(
            (f: any) => f.is_folder && f.obfuscated_name === this.currentPath
          );
          if (folderInResponse) {
            this.currentFolderIsShared = this.isSharedFolder(folderInResponse);
          }
          // If not found, keep existing value (set by navigateToFolder or default false)
        }
      } else {
        // We're at root level, not in a folder
        this.currentFolderIsShared = false;
      }

      // Hide loading immediately after files are loaded
      this.setLoading(false);

      // Force multiple change detection cycles to ensure UI updates
      setTimeout(() => {
        this.ngZone.run(() => {
          this.cdr.markForCheck();
          this.cdr.detectChanges();
        });
      }, 50);

      // One more detection cycle
      setTimeout(() => {
        this.ngZone.run(() => {
          this.cdr.markForCheck();
          this.cdr.detectChanges();
        });
      }, 150);
    } catch (error: any) {
      if (generation !== this.loadFolderGeneration) {
        return;
      }
      this.showError('Kunne ikke indlæse mappe: ' + error.message);
      this.currentFiles = [];
      this.currentFolders = [];
      this.setLoading(false);
      this.cdr.detectChanges();
    }
  }

  // ==================== Helper Methods ====================

  /**
   * Safely set loading state with automatic timeout fail-safe
   */
  private setLoading(loading: boolean, timeout: number = 30000): void {

    // Clear existing timeout
    if (this.loadingTimeout) {
      clearTimeout(this.loadingTimeout);
      this.loadingTimeout = null;
    }

    // Only change state if it's actually different to prevent race conditions
    if (this.isLoading !== loading) {
      this.isLoading = loading;

      // Force change detection with multiple approaches
      this.ngZone.run(() => {
        this.cdr.markForCheck();
        this.cdr.detectChanges();
      });

      // Additional change detection after a short delay
      setTimeout(() => {
        this.ngZone.run(() => {
          this.cdr.markForCheck();
          this.cdr.detectChanges();
        });
      }, 25);

      // One more for good measure
      setTimeout(() => {
        this.ngZone.run(() => {
          this.cdr.detectChanges();
        });
      }, 100);
    } else {
      // Still trigger change detection to refresh UI
      this.ngZone.run(() => {
        this.cdr.markForCheck();
        this.cdr.detectChanges();
      });
    }

    // Set fail-safe timeout to prevent stuck loading
    if (loading) {
      this.loadingTimeout = setTimeout(() => {
        console.warn(
          '[DunaDocuments] Loading timeout reached, forcing isLoading = false'
        );
        this.ngZone.run(() => {
          this.isLoading = false;
          this.loadingTimeout = null;
          this.cdr.markForCheck();
          this.cdr.detectChanges();
        });
      }, timeout);
    }
  }

  /**
   * Refresh file list
   */
  refreshFileManager(): void {
    this.loadFolder(this.currentPath);
  }

  /**
   * Force reset loading state (emergency cleanup)
   */
  forceResetLoading(): void {
    console.warn('[DunaDocuments] Force reset loading state');
    this.setLoading(false);
  }

  /**
   * Show success message
   */
  showSuccess(message: string): void {
    // TODO: Implement toast/snackbar notification
  }

  /**
   * Show error message
   */
  showError(message: string): void {
    this.errorMessage = message;
    console.error('[DunaDocuments] Error:', message);
    // TODO: Implement toast/snackbar notification

    setTimeout(() => {
      this.errorMessage = null;
    }, 5000);
  }

  /**
   * Download file by URL
   */
  private downloadFileByUrl(url: string, filename: string): void {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  /**
   * Extract ID from path (e.g., /Events/123 -> 123)
   */
  private extractIdFromPath(path: string): number | undefined {
    const parts = path.split('/').filter((p) => p);
    if (parts.length >= 2) {
      const id = parseInt(parts[1], 10);
      return isNaN(id) ? undefined : id;
    }
    return undefined;
  }

  /**
   * Format file size
   */
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  }

  /**
   * Format date
   */
  formatDate(dateString: string): string {
    if (!dateString) return '-';

    const date = new Date(dateString);
    return date.toLocaleDateString('da-DK', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  /**
   * Check if user can upload to current folder
   */
  canUpload(): boolean {

    // If we're in event context, allow upload. At event root, files go to the chosen role panel.
    if (this.isInEventContext) {
      return true;
    }

    if (this.currentPath === '/') {
      return false;
    }

    // Check if user is trying to access Global folder
    if (this.currentPath.startsWith('/Globalt')) {
      const canUpload = this.isSuperadmin();
      return canUpload;
    }

    // Disable uploads to trash folder
    if (
      this.currentPath.startsWith('/Papirkurv') ||
      this.currentPath.startsWith('/Trash')
    ) {
      return false;
    }

    // Allow upload for Organizations for all users (including subfolders)
    // Check if we're in Organizations scope
    const currentScope = this.currentScope;
    const canUpload =
      currentScope.type === 'organization' || currentScope.type === 'event';
    return canUpload;
  }

  /**
   * Check if current user is Superadmin
   */
  isSuperadmin(): boolean {
    const currentUser = this.authService.getCurrentUser();
    const userRoles = currentUser?.roles || [];
    return userRoles.includes('superadmin');
  }

  /**
   * Check if we're currently in an event scope (either URL-based or folder navigation)
   */
  isEventScope(): boolean {
    return (
      this.isInEventContext ||
      (this.currentScope.type === 'event' && this.currentScope.id !== undefined)
    );
  }

  isEventRootView(): boolean {
    return (
      this.isEventScope() &&
      this.folderHierarchy.length === 0 &&
      (this.currentPath === '/' ||
        this.currentPath === '/Events' ||
        this.currentPath === '/Events/Deltager' ||
        this.currentPath === '/Events/Instruktør')
    );
  }

  getEventFileRole(file: FileItemDTO): 'participant' | 'instructor' {
    return file.event_role === 'instructor' ? 'instructor' : 'participant';
  }

  getEventRoleFiles(role: 'participant' | 'instructor'): FileItemDTO[] {
    return this.currentFiles.filter(
      (file) => this.getEventFileRole(file) === role
    );
  }

  getEventRoleFolders(role: 'participant' | 'instructor'): FileItemDTO[] {
    return this.currentFolders.filter(
      (folder) => this.getEventFileRole(folder) === role
    );
  }

  uploadForRole(role: 'participant' | 'instructor'): void {
    this.uploadTargetRole = role;
    this.selectedEventRole = role;
    this.fileInput?.nativeElement.click();
  }

  showCreateFolderForRole(role: 'participant' | 'instructor'): void {
    this.uploadTargetRole = role;
    this.selectedEventRole = role;
    this.showCreateFolder();
  }

  async showShareFilesForRole(role: 'participant' | 'instructor'): Promise<void> {
    this.shareTargetRole = role;
    this.selectedEventRole = role;
    this.shareRoleLocked = true;
    await this.showShareFiles();
  }

  async navigateToFolderFromRole(
    folder: FileItemDTO,
    role: 'participant' | 'instructor'
  ): Promise<void> {
    this.selectedEventRole = role;
    this.uploadTargetRole = role;
    await this.navigateToFolder(folder);
  }

  /**
   * Check if we're currently in a location scope
   */
  isLocationScope(): boolean {
    return (
      this.currentScope.type === 'location' &&
      this.currentScope.id !== undefined
    );
  }

  /**
   * Check if file is from Organization or Global scope (shared file)
   */
  isSharedFile(file: FileItemDTO): boolean {
    // A file/folder is shared if it has is_readonly = true
    // This indicates it was shared from organization/global to event/location
    return file.is_readonly === true;
  }

  /**
   * Check if folder is shared (has access record in current scope)
   * Same logic as isSharedFile but named for clarity when working with folders
   */
  isSharedFolder(folder: FileItemDTO): boolean {
    return this.isSharedFile(folder);
  }

  /**
   * Get file origin icon based on scope
   */
  getFileOriginIcon(file: FileItemDTO): string {
    // For now, since we don't have scope_type in the response,
    // we'll show a generic icon for readonly files
    if (file.is_readonly) {
      return 'link';
    }
    return 'insert_drive_file';
  }

  /**
   * Get file origin label
   */
  getFileOriginLabel(file: FileItemDTO): string {
    // For now, since we don't have scope_type in the response,
    // we'll show a generic label for readonly files
    if (file.is_readonly) {
      return 'Delt';
    }
    return 'Lokalt';
  }

  /**
   * Check if file operations are allowed (no delete/modify for shared files)
   */
  /**
   * Check if file is ready for viewing/downloading
   */
  isFileReady(file: FileItemDTO): boolean {
    // Only 'ready' status files can be viewed
    return file.status === 'ready' || file.status === 'completed';
  }

  /**
   * Get human-readable status label
   */
  getStatusLabel(status: string): string {
    const statusLabels: { [key: string]: string } = {
      initializing: 'Initialiserer',
      uploading: 'Uploader',
      queued: 'I kø',
      processing: 'Behandler',
      ready: 'Klar',
      completed: 'Færdig',
      failed: 'Fejlet',
    };
    return statusLabels[status] || status || 'Ukendt';
  }

  /**
   * Get status badge color class
   */
  getStatusBadgeClass(status: string): string {
    const statusClasses: { [key: string]: string } = {
      initializing: 'status-initializing',
      uploading: 'status-uploading',
      queued: 'status-queued',
      processing: 'status-processing',
      ready: 'status-ready',
      completed: 'status-ready',
      failed: 'status-failed',
    };
    return statusClasses[status] || 'status-unknown';
  }

  canModifyFile(file: FileItemDTO): boolean {
    // Can't modify files that are readonly (shared from organization/global)
    if (file.is_readonly) {
      return false;
    }

    // Can't modify files that are in a shared folder (accessed through folder reference)
    // Files in shared folders should be protected from deletion
    if (this.currentFolderIsShared) {
      return false;
    }

    return true;
  }

  /**
   * Get info text for disabled folders
   */
  getFolderInfo(folderName: string): string {
    switch (folderName) {
      case 'Begivenheder':
        return 'Gå direkte til begivenheden for at uploade filer';
      case 'Lokationer':
        return 'Kommer snart';
      default:
        return '';
    }
  }

  /**
   * Toggle help section visibility
   */
  toggleHelp(): void {
    this.helpExpanded = !this.helpExpanded;
  }

  // ==================== Virtual Folder Management ====================

  /**
   * Show create folder dialog
   */
  showCreateFolder(): void {
    this.showCreateFolderDialog = true;
    this.newFolderName = '';
    this.folderNameInvalidChars = [];
  }

  /**
   * Hide create folder dialog
   */
  hideCreateFolder(): void {
    this.showCreateFolderDialog = false;
    this.newFolderName = '';
    this.folderNameInvalidChars = [];
  }

  /**
   * Validate folder name against backend rules
   * Allows Unicode letters (including Æ, Ø, Å), numbers, spaces, hyphens, and underscores
   */
  private isValidFolderName(name: string): boolean {
    // Match backend regex: /^[\p{L}\p{N}\s\-_]+$/u
    // In JavaScript, we use Unicode property escapes
    const folderNameRegex = /^[\p{L}\p{N}\s\-_]+$/u;
    return folderNameRegex.test(name);
  }

  /**
   * Validate folder name and extract invalid characters
   * Returns array of invalid characters found
   */
  private getInvalidFolderNameChars(name: string): string[] {
    if (!name) return [];

    const allowedRegex = /[\p{L}\p{N}\s\-_]/u;
    const invalidChars: string[] = [];
    const seenChars = new Set<string>();

    // Check each character
    for (const char of name) {
      if (!allowedRegex.test(char) && !seenChars.has(char)) {
        invalidChars.push(char);
        seenChars.add(char);
      }
    }

    return invalidChars;
  }

  /**
   * Validate folder name in real-time as user types
   */
  validateFolderNameInput(): void {
    const name = this.newFolderName;
    this.folderNameInvalidChars = this.getInvalidFolderNameChars(name);
  }

  /**
   * Create a new virtual folder
   */
  async createFolder(): Promise<void> {
    if (this.isCreatingFolder) {
      return;
    }

    const trimmedName = this.newFolderName.trim();

    if (!trimmedName) {
      this.showError('Folder navn er påkrævet');
      return;
    }

    // Validate folder name format (matches backend validation)
    if (!this.isValidFolderName(trimmedName)) {
      this.showError(
        'Folder navn kan kun indeholde bogstaver (inkl. Æ, Ø, Å), tal, mellemrum, bindestreger og understregninger'
      );
      return;
    }

    this.isCreatingFolder = true;
    try {
      // Use currentScope for type - backend will get organization ID from user request
      let scopeType = (this.currentScope.type || 'organization') as
        | 'global'
        | 'organization'
        | 'location'
        | 'event'
        | 'user'
        | undefined;
      let scopeId: string | number | undefined = this.currentScope.id;

      // For event scope: API requires a valid UUID. When in event context use detectedEventId;
      // when path is /Events/Deltager or /Events/Instruktør, currentScope.id is undefined.
      if (scopeType === 'event') {
        scopeId = this.isInEventContext
          ? this.detectedEventId ?? undefined
          : this.currentScope.id;
        const validEventId =
          scopeId != null &&
          typeof scopeId === 'string' &&
          scopeId.length === 36;
        if (!validEventId) {
          this.showError(
            'Kunne ikke oprette folder: Event-ID mangler. Opdater siden og prøv igen.'
          );
          this.isCreatingFolder = false;
          return;
        }
      }

      // Determine correct parent_folder
      let parentFolder: string | undefined = undefined;

      if (
        this.currentPath &&
        !this.currentPath.startsWith('/') &&
        this.currentPath !== '/'
      ) {
        // We're in a subfolder, use the folder's obfuscated_name as parent
        parentFolder = this.currentPath;
      }
      // For top-level views (/Organizations, /Globalt, etc.), parentFolder remains null

      // Event role for event-scoped folder (matches upload behavior)
      let eventRole: string | undefined;
      if (scopeType === 'event') {
        if (this.uploadTargetRole) {
          eventRole = this.uploadTargetRole;
        } else if (this.selectedEventRole) {
          eventRole = this.selectedEventRole;
        } else if (
          this.currentPath === '/Events/Deltager' ||
          (parentFolder && parentFolder.includes('Deltager'))
        ) {
          eventRole = 'participant';
        } else if (
          this.currentPath === '/Events/Instruktør' ||
          (parentFolder && parentFolder.includes('Instruktør'))
        ) {
          eventRole = 'instructor';
        }
      }

      await this.mediaService.createFolder(
        trimmedName,
        parentFolder,
        scopeType,
        scopeId,
        eventRole
      );

      this.showSuccess('Folder oprettet');
      this.hideCreateFolder();
      await this.loadFolder(this.currentPath);
    } catch (error: any) {
      this.showError('Kunne ikke oprette folder: ' + error.message);
    } finally {
      this.isCreatingFolder = false;
    }
  }

  /**
   * Navigate into a folder
   */
  async navigateToFolder(folder: FileItemDTO): Promise<void> {
    // Check if folder is shared before navigating (to protect files inside)
    this.currentFolderIsShared = this.isSharedFolder(folder);

    // Add folder to hierarchy
    this.folderHierarchy.push({
      name:
        folder.display_name || folder.original_name || folder.obfuscated_name,
      obfuscatedName: folder.obfuscated_name,
    });

    // Use the folder's obfuscated_name as the navigation target
    await this.loadFolder(folder.obfuscated_name);
  }

  /**
   * Delete a folder
   */
  async deleteFolder(folder: FileItemDTO): Promise<void> {
    if (
      !confirm(`Er du sikker på at slette mappen "${folder.display_name}"?`)
    ) {
      return;
    }

    try {
      this.setLoading(true);
      await this.mediaService.deleteFolder(folder.obfuscated_name);
      this.showSuccess('Folder slettet');
      await this.loadFolder(this.currentPath);
    } catch (error: any) {
      this.showError('Kunne ikke slette folder: ' + error.message);
    } finally {
      this.setLoading(false);
    }
  }

  /**
   * Restore a folder from trash
   */
  async restoreFolder(folder: FileItemDTO): Promise<void> {
    if (!confirm(`Gendan mappen "${folder.display_name}" fra papirkurven?`)) {
      return;
    }

    try {
      this.setLoading(true);
      await this.mediaService.restoreFolder(folder.obfuscated_name);
      this.showSuccess('Folder gendannet fra papirkurv');
      await this.loadFolder(this.currentPath);
    } catch (error: any) {
      this.showError('Kunne ikke gendanne folder: ' + error.message);
    } finally {
      this.setLoading(false);
    }
  }

  /**
   * Rename a folder
   */
  async renameFolder(folder: FileItemDTO): Promise<void> {
    const newName = prompt(
      `Omdøb mappen "${folder.display_name}":`,
      folder.display_name
    );
    if (!newName || newName === folder.display_name) {
      return;
    }

    const trimmedName = newName.trim();

    // Validate folder name format (matches backend validation)
    if (!this.isValidFolderName(trimmedName)) {
      this.showError(
        'Folder navn kan kun indeholde bogstaver (inkl. Æ, Ø, Å), tal, mellemrum, bindestreger og understregninger'
      );
      return;
    }

    try {
      this.setLoading(true);
      await this.mediaService.renameFolder(folder.obfuscated_name, trimmedName);
      this.showSuccess('Folder omdøbt');
      await this.loadFolder(this.currentPath);
    } catch (error: any) {
      this.showError('Kunne ikke omdøbe folder: ' + error.message);
    } finally {
      this.setLoading(false);
    }
  }

  /**
   * Show file information modal
   */
  async showFileInfo(file: FileItemDTO): Promise<void> {
    this.selectedFileInfo = file;
    this.showFileInfoDialog = true;
    this.editingDisplayName = false;
  }

  /**
   * Hide file info modal
   */
  hideFileInfo(): void {
    this.showFileInfoDialog = false;
    this.selectedFileInfo = null;
    this.editingDisplayName = false;
  }

  /**
   * Start editing display name
   */
  startEditingDisplayName(): void {
    this.editingDisplayName = true;
  }

  /**
   * Save display name changes
   */
  async saveDisplayName(): Promise<void> {
    if (!this.selectedFileInfo) return;

    const newName = this.selectedFileInfo.display_name?.trim();
    if (!newName) {
      this.showError('Display name cannot be empty');
      return;
    }

    try {
      this.setLoading(true);
      await this.mediaService.renameFile(
        this.selectedFileInfo.obfuscated_name,
        newName
      );
      this.selectedFileInfo.display_name = newName;
      this.editingDisplayName = false;
      this.showSuccess('Display name opdateret');
      await this.loadFolder(this.currentPath);
    } catch (error: any) {
      this.showError('Kunne ikke opdatere display name: ' + error.message);
    } finally {
      this.setLoading(false);
    }
  }

  /**
   * Cancel editing display name
   */
  cancelEditingDisplayName(): void {
    this.editingDisplayName = false;
  }

  /**
   * Show move file dialog
   */
  async showMoveFile(file: FileItemDTO): Promise<void> {
    await this.openMoveFilesDialog([file]);
  }

  async showMoveForSelectedFiles(): Promise<void> {
    const files = this.getSelectedFiles().filter(
      (f) => this.canModifyFile(f) && !this.isSharedFile(f)
    );
    if (files.length === 0) {
      return;
    }
    await this.openMoveFilesDialog(files);
  }

  private async openMoveFilesDialog(files: FileItemDTO[]): Promise<void> {
    this.filesToMove = files;

    try {
      this.setLoading(true);
      const requestData: any = {
        scope_type:
          this.currentScope.type === 'organization'
            ? 'organization'
            : undefined,
        include_trash: false,
      };

      const validScopeId = this.currentScope.type
        ? this.getScopeId(this.currentScope.type, this.currentScope.id)
        : undefined;
      if (validScopeId !== undefined) {
        requestData.scope_id = validScopeId;
      }

      const response: any = await this.mediaService.listFiles(requestData);

      this.availableFolders = this.extractListedFiles(response).filter(
        (f) => f.is_folder
      );
      this.showMoveFileDialog = true;
    } catch (error: any) {
      this.showError('Kunne ikke indlæse mapper: ' + error.message);
    } finally {
      this.setLoading(false);
    }
  }

  /**
   * Hide move file dialog
   */
  hideMoveFile(): void {
    this.showMoveFileDialog = false;
    this.filesToMove = [];
    this.availableFolders = [];
  }

  /**
   * Move file(s) to selected folder
   */
  async moveFileToFolder(targetFolder: FileItemDTO): Promise<void> {
    if (this.filesToMove.length === 0) {
      return;
    }

    const count = this.filesToMove.length;

    try {
      this.setLoading(true);
      let failed = 0;
      for (const file of this.filesToMove) {
        try {
          await this.mediaService.moveFileToFolder(
            file.obfuscated_name,
            targetFolder.obfuscated_name
          );
        } catch {
          failed++;
        }
      }
      this.clearFileSelection();
      this.hideMoveFile();
      if (failed === 0) {
        this.showSuccess(
          count === 1
            ? `Fil flyttet til "${targetFolder.display_name}"`
            : `${count} filer flyttet til "${targetFolder.display_name}"`
        );
      } else {
        this.showError('Nogle filer kunne ikke flyttes');
      }
      await this.loadFolder(this.currentPath);
    } catch (error: any) {
      this.showError('Kunne ikke flytte fil: ' + error.message);
    } finally {
      this.setLoading(false);
    }
  }

  /**
   * Move file(s) to root folder
   */
  async moveToRootFolder(): Promise<void> {
    if (this.filesToMove.length === 0) {
      return;
    }

    const count = this.filesToMove.length;

    try {
      this.setLoading(true);
      let failed = 0;
      for (const file of this.filesToMove) {
        try {
          await this.mediaService.moveFileToFolder(file.obfuscated_name, '/');
        } catch {
          failed++;
        }
      }
      this.clearFileSelection();
      this.hideMoveFile();
      if (failed === 0) {
        this.showSuccess(
          count === 1 ? 'Fil flyttet til root mappe' : `${count} filer flyttet til root mappe`
        );
      } else {
        this.showError('Nogle filer kunne ikke flyttes');
      }
      await this.loadFolder(this.currentPath);
    } catch (error: any) {
      this.showError('Kunne ikke flytte fil: ' + error.message);
    } finally {
      this.setLoading(false);
    }
  }

  /**
   * Upload file to current folder
   */
  async uploadFilesToCurrentFolder(files: FileList): Promise<void> {
    if (this.isUploading) {
      return;
    }

    try {
      this.isUploading = true;
      this.uploadProgress = 0;
      this.uploadStatus = 'Preparing upload...';
      this.createUploadProgressContainer();
      this.updateUploadProgressContainer();

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        this.uploadStatus = `Uploading ${file.name} (${i + 1}/${files.length})`;
        this.updateUploadProgressContainer();

        // Use currentScope for type - backend will get organization ID from user request
        let scopeType:
          | 'global'
          | 'organization'
          | 'location'
          | 'event'
          | 'user'
          | undefined;
        let scopeId: string | number | undefined;

        if (this.isInEventContext) {
          scopeType = 'event';
          scopeId = this.detectedEventId || undefined;
        } else {
          scopeType = (this.currentScope.type || 'organization') as
            | 'global'
            | 'organization'
            | 'location'
            | 'event'
            | 'user'
            | undefined;
          scopeId = this.currentScope.id;
        }

        // Determine target folder for upload
        // If currentPath is an obfuscated_name (starts with 'folder_' or is a UUID), we're in a subfolder
        const isInSubfolder =
          this.currentPath &&
          !this.currentPath.startsWith('/') &&
          this.currentPath.length > 10;
        const targetFolder = isInSubfolder ? this.currentPath : '/';

        // Get valid scope ID (undefined is fine for optional parameter)
        const validScopeId = scopeType
          ? this.getScopeId(scopeType, scopeId)
          : undefined;

        // CRITICAL: For events, we MUST have a valid UUID - don't allow upload without it
        if (scopeType === 'event') {
          if (
            !validScopeId ||
            typeof validScopeId !== 'string' ||
            validScopeId.length !== 36
          ) {
            console.error(
              '[DunaDocuments] BLOCKING UPLOAD: Invalid event UUID for upload:',
              {
                validScopeId,
                typeof: typeof validScopeId,
                detectedEventId: this.detectedEventId,
                detectedEventUuid: this.detectedEventUuid,
                scopeId,
              }
            );
            this.showError(
              `Cannot upload to event: Invalid UUID (${validScopeId}). Please refresh the page.`
            );
            this.isUploading = false;
            return;
          }

          // Must have a role when uploading to an event
          const isAtEventRoot =
            this.currentPath === '/' || this.currentPath === '/Events';
          if (
            isAtEventRoot &&
            !this.uploadTargetRole &&
            !this.selectedEventRole
          ) {
            this.showError(
              'Vælg om filen er til deltagere eller instruktører.'
            );
            this.isUploading = false;
            return;
          }
        }

        // Determine event_role for event uploads
        let eventRole: string | undefined;
        if (scopeType === 'event') {
          if (this.uploadTargetRole) {
            eventRole = this.uploadTargetRole;
          } else if (this.selectedEventRole) {
            eventRole = this.selectedEventRole;
          } else if (
            this.currentPath === '/Events/Deltager' ||
            targetFolder.includes('Deltager')
          ) {
            eventRole = 'participant';
          } else if (
            this.currentPath === '/Events/Instruktør' ||
            targetFolder.includes('Instruktør')
          ) {
            eventRole = 'instructor';
          }
          if (!eventRole) {
            eventRole = 'participant';
          }
        }

        const obfuscatedName = await this.mediaService.uploadFileToFolder(
          file,
          targetFolder,
          scopeType,
          validScopeId,
          (progress) => {
            this.uploadProgress = progress;
            this.uploadStatus = `Uploading ${file.name} (${i + 1}/${
              files.length
            }) - ${Math.round(progress)}%`;
            this.updateUploadProgressContainer();
          },
          (status) => {
            this.uploadStatus = `Uploading ${file.name} (${i + 1}/${
              files.length
            }) - ${status}`;
            this.updateUploadProgressContainer();
          },
          eventRole
        );

      }

      this.uploadProgress = 100;
      this.uploadStatus = `Upload complete! ${files.length} file(s) uploaded`;
      this.updateUploadProgressContainer();

      this.showSuccess(`${files.length} fil(er) uploaded`);
      await this.loadFolder(this.currentPath);

      // Reset upload UI after a short delay
      setTimeout(() => {
        this.isUploading = false;
        this.uploadProgress = 0;
        this.uploadStatus = '';
        this.removeUploadProgressContainer();
      }, 1500);
    } catch (error: any) {
      this.showError('Upload fejlede: ' + this.formatUploadInitError(error));
      this.isUploading = false;
      this.uploadProgress = 0;
      this.uploadStatus = '';
      this.removeUploadProgressContainer();
      await this.loadFolder(this.currentPath);
    }
  }

  /**
   * Get scope type from current path
   */
  private getScopeTypeFromPath(
    path: string
  ): 'global' | 'organization' | 'location' | 'event' | 'user' | undefined {
    if (path.startsWith('/Globalt')) return 'global';
    if (path.startsWith('/Papirkurv')) return undefined; // Trash doesn't need scope
    if (path.startsWith('/Events')) return 'event';
    if (path.startsWith('/Locations')) return 'location';
    if (path.startsWith('/Organizations')) return 'organization';
    return 'organization'; // Default
  }

  /**
   * Get scope ID from current path
   */
  private getScopeIdFromPath(path: string): number | undefined {
    if (path.startsWith('/Events/') || path.startsWith('/Locations/')) {
      return this.extractIdFromPath(path);
    }
    return undefined;
  }

  /**
   * Get display path for breadcrumb
   */
  getDisplayPath(): string {
    if (this.currentPath === '/' || this.folderHierarchy.length === 0) {
      return this.getScopeDisplayName();
    }

    const prefix = this.getScopeDisplayName();
    const folderPath = this.folderHierarchy.map((f) => f.name).join(' > ');
    return prefix + (folderPath ? ' > ' + folderPath : '');
  }

  /**
   * Get friendly scope display name
   */
  private getScopeDisplayName(): string {
    if (this.isInEventContext && this.detectedEventUuid) {
      return `Event (${this.detectedEventUuid.substring(0, 8)}...)`;
    }

    switch (this.currentScope.type) {
      case 'organization':
        return 'Organisation';
      case 'global':
        return 'Globalt';
      case 'event':
        return `Event ${this.currentScope.id}`;
      case 'location':
        return `Location ${this.currentScope.id}`;
      case 'trash':
        return 'Papirkurv';
      default:
        return 'Organisation';
    }
  }

  /**
   * Go back one level in folder hierarchy
   */
  async goBack(): Promise<void> {
    if (this.folderHierarchy.length > 0) {
      this.folderHierarchy.pop();

      if (this.folderHierarchy.length === 0) {
        // Go back to scope root
        this.currentFolderIsShared = false;
        if (this.isInEventContext) {
          this.selectedEventRole = null;
          this.uploadTargetRole = null;
          await this.loadFolder('/');
        } else {
          await this.changeFolder('/' + this.getScopeDisplayName());
        }
      } else {
        // Go to parent folder - will be updated by loadFolder
        const parentFolder =
          this.folderHierarchy[this.folderHierarchy.length - 1];
        await this.loadFolder(parentFolder.obfuscatedName);
      }
    } else {
      // Go back to scope root
      this.currentFolderIsShared = false;
      await this.changeFolder('/' + this.getScopeDisplayName());
    }
  }

  // ==================== File Sharing Methods ====================

  /**
   * Show dialog to share files from Organization/Global to current event
   */
  async showShareFiles(): Promise<void> {
    if (!this.isEventScope() && !this.isLocationScope()) {
      this.showError(
        'Fildeling er kun tilgængelig i begivenheder og lokationer'
      );
      return;
    }

    this.showShareFileDialog = true;
    this.fileToShare = null;
    this.fileSearchQuery = '';
    this.selectedFileCategory = 'all';
    this.shareBrowsePath = [];
    await this.loadShareBrowseRoot();
  }

  get shareTargetRoleLabel(): string {
    return this.shareTargetRole === 'instructor' ? 'instruktører' : 'deltagere';
  }

  get shareBrowseFolders(): FileItemDTO[] {
    return this.filteredShareFiles
      .filter((file) => file.is_folder)
      .sort((a, b) =>
        (a.display_name || a.original_name || '').localeCompare(
          b.display_name || b.original_name || '',
          'da'
        )
      );
  }

  get shareBrowseFileItems(): FileItemDTO[] {
    return this.filteredShareFiles
      .filter((file) => !file.is_folder)
      .sort((a, b) =>
        (a.display_name || a.original_name || '').localeCompare(
          b.display_name || b.original_name || '',
          'da'
        )
      );
  }

  get isShareBrowsingRoot(): boolean {
    return this.shareBrowsePath.length === 0;
  }

  private async loadShareBrowseRoot(): Promise<void> {
    this.shareIsBrowsing = true;
    this.refreshShareView();
    try {
      const [orgResult, globalResult] = await Promise.allSettled([
        this.loadShareScopeFiles('organization'),
        this.loadShareScopeFiles('global'),
      ]);

      const orgFiles =
        orgResult.status === 'fulfilled' ? orgResult.value : [];
      const globalFiles =
        globalResult.status === 'fulfilled' ? globalResult.value : [];

      if (!this.showShareFileDialog) {
        return;
      }

      this.availableShareFiles = [...orgFiles, ...globalFiles];
      this.filterFiles();

      if (orgResult.status === 'rejected' && globalResult.status === 'rejected') {
        this.showError('Kunne ikke indlæse filer til fildeling');
      }
    } catch (error: any) {
      this.showError(
        'Kunne ikke indlæse filer til fildeling: ' + (error?.message || error)
      );
      if (this.availableShareFiles.length === 0) {
        this.filteredShareFiles = [];
      }
    } finally {
      this.shareIsBrowsing = false;
      this.refreshShareView();
    }
  }

  private async loadShareScopeFiles(
    scopeType: 'organization' | 'global'
  ): Promise<FileItemDTO[]> {
    try {
      const requestData: {
        scope_type: 'organization' | 'global';
        scope_id?: string | number;
      } = {
        scope_type: scopeType,
      };
      if (scopeType === 'organization') {
        const orgId = this.getOrganizationScopeId();
        if (orgId !== undefined) {
          requestData.scope_id = orgId;
        }
      }

      const response: any = await this.mediaService.listFiles(requestData);
      const files = this.extractListedFiles(response);
      return files.map((file) => ({
        ...file,
        scope_type: file.scope_type || scopeType,
      }));
    } catch (error) {
      console.error(
        `[DunaDocuments] Could not load ${scopeType} files for sharing`,
        error
      );
      return [];
    }
  }

  private async listShareFolderContents(
    scopeType: string,
    parentFolder: string
  ): Promise<any> {
    const requestData: {
      scope_type: string;
      parent_folder: string;
      scope_id?: string | number;
    } = {
      scope_type: scopeType,
      parent_folder: parentFolder,
    };
    if (scopeType === 'organization') {
      const orgId = this.getOrganizationScopeId();
      if (orgId !== undefined) {
        requestData.scope_id = orgId;
      }
    }
    return this.mediaService.listFiles(requestData);
  }

  private extractListedFiles(response: any): FileItemDTO[] {
    let files: FileItemDTO[] = [];
    if (Array.isArray(response?.files)) {
      files = response.files;
    } else if (Array.isArray(response?.data?.files)) {
      files = response.data.files;
    } else if (Array.isArray(response?.data)) {
      files = response.data;
    }
    return this.dedupeListedFiles(files);
  }

  /**
   * One row per upload — list_files JOINs can return duplicates for the same upload_id.
   */
  private dedupeListedFiles(files: FileItemDTO[]): FileItemDTO[] {
    const byKey = new Map<string, FileItemDTO>();
    for (const file of files) {
      const key =
        file.upload_id != null
          ? `id:${file.upload_id}`
          : file.obfuscated_name
            ? `name:${file.obfuscated_name}`
            : '';
      if (!key) {
        continue;
      }
      const existing = byKey.get(key);
      if (!existing) {
        byKey.set(key, file);
        continue;
      }
      if (!existing.event_role && file.event_role) {
        byKey.set(key, file);
      }
    }
    return Array.from(byKey.values());
  }

  private getOrganizationScopeId(): string | number | undefined {
    const currentUser = this.authService.getCurrentUser() as any;
    const raw =
      currentUser?.user_info?.organizations_id ??
      currentUser?.organizations_id;
    if (raw === undefined || raw === null || raw === '') {
      return undefined;
    }
    const asNumber = Number(raw);
    return Number.isFinite(asNumber) ? asNumber : raw;
  }

  private refreshShareView(): void {
    this.ngZone.run(() => {
      this.cdr.markForCheck();
      this.cdr.detectChanges();
    });
    setTimeout(() => {
      this.ngZone.run(() => {
        this.cdr.markForCheck();
        this.cdr.detectChanges();
      });
    }, 50);
  }

  async openShareBrowseFolder(folder: FileItemDTO, event?: Event): Promise<void> {
    event?.stopPropagation();
    this.shareIsBrowsing = true;
    this.fileToShare = null;
    this.fileSearchQuery = '';
    this.refreshShareView();
    try {
      const scopeType = folder.scope_type || 'organization';
      const response = await this.listShareFolderContents(
        scopeType,
        folder.obfuscated_name
      );
      if (!this.showShareFileDialog) {
        return;
      }
      this.shareBrowsePath = [
        ...this.shareBrowsePath,
        {
          name: folder.display_name || folder.original_name || 'Mappe',
          obfuscatedName: folder.obfuscated_name,
          scopeType,
        },
      ];
      this.selectedFileCategory =
        scopeType === 'global' ? 'global' : 'organization';
      this.availableShareFiles = this.extractListedFiles(response).map(
        (file) => ({
          ...file,
          scope_type: scopeType,
        })
      );
      this.filterFiles();
    } catch (error: any) {
      this.showError('Kunne ikke åbne mappen: ' + (error?.message || error));
    } finally {
      this.shareIsBrowsing = false;
      this.refreshShareView();
    }
  }

  async goToShareBrowseRoot(): Promise<void> {
    this.shareBrowsePath = [];
    this.selectedFileCategory = 'all';
    this.fileToShare = null;
    this.fileSearchQuery = '';
    await this.loadShareBrowseRoot();
  }

  async goToShareBrowseIndex(index: number): Promise<void> {
    if (index < 0) {
      await this.goToShareBrowseRoot();
      return;
    }
    const target = this.shareBrowsePath[index];
    if (!target) {
      return;
    }
    this.shareIsBrowsing = true;
    this.fileToShare = null;
    this.fileSearchQuery = '';
    this.refreshShareView();
    try {
      const response = await this.listShareFolderContents(
        target.scopeType,
        target.obfuscatedName
      );
      if (!this.showShareFileDialog) {
        return;
      }
      this.shareBrowsePath = this.shareBrowsePath.slice(0, index + 1);
      this.selectedFileCategory =
        target.scopeType === 'global' ? 'global' : 'organization';
      this.availableShareFiles = this.extractListedFiles(response).map(
        (file) => ({
          ...file,
          scope_type: target.scopeType,
        })
      );
      this.filterFiles();
    } catch (error: any) {
      this.showError('Kunne ikke åbne mappen: ' + (error?.message || error));
    } finally {
      this.shareIsBrowsing = false;
      this.refreshShareView();
    }
  }

  setShareCategory(category: 'all' | 'organization' | 'global'): void {
    this.selectedFileCategory = category;
    this.filterFiles();
  }

  getShareItemIcon(file: FileItemDTO): string {
    if (file.is_folder) {
      return 'folder';
    }
    if (file.is_video) {
      return 'videocam';
    }
    const mime = file.mime_type || '';
    if (mime.includes('pdf')) {
      return 'picture_as_pdf';
    }
    if (mime.startsWith('image/')) {
      return 'image';
    }
    if (mime.includes('spreadsheet') || mime.includes('excel')) {
      return 'table_chart';
    }
    if (mime.includes('word') || mime.includes('document')) {
      return 'description';
    }
    return 'insert_drive_file';
  }

  getShareOriginLabel(file: FileItemDTO): string {
    return file.scope_type === 'global' ? 'Globalt' : 'Organisation';
  }

  /**
   * Hide share files dialog
   */
  hideShareFiles(): void {
    this.showShareFileDialog = false;
    this.availableShareFiles = [];
    this.filteredShareFiles = [];
    this.fileToShare = null;
    this.fileSearchQuery = '';
    this.selectedFileCategory = 'all';
    this.shareBrowsePath = [];
    this.shareRoleLocked = false;
    this.shareIsBrowsing = false;
  }

  /**
   * Filter files based on search query, category, and role
   */
  filterFiles(): void {
    let files = [...this.availableShareFiles];

    // Filter by category
    if (this.selectedFileCategory !== 'all') {
      files = files.filter(
        (file) => file.scope_type === this.selectedFileCategory
      );
    }

    // Filter by role (for events only)
    if (this.isEventScope()) {
      files = files.filter((file) => {
        // For now, show all files for both roles
        // In the future, this could filter by file.access_level or similar
        return true;
      });
    }

    // Filter by search query
    if (this.fileSearchQuery.trim()) {
      const query = this.fileSearchQuery.toLowerCase().trim();
      files = files.filter((file) => {
        const fileName = (
          file.display_name ||
          file.original_name ||
          ''
        ).toLowerCase();
        return fileName.includes(query);
      });
    }

    this.filteredShareFiles = files;
  }

  /**
   * Get file count for a specific role
   */
  getRoleFileCount(role: string): number {

    if (!this.isEventScope()) {
      return this.availableShareFiles.length;
    }

    // Count files that would be available for this role
    let count = 0;
    ['organization', 'global'].forEach((category) => {
      const categoryFiles = this.availableShareFiles.filter(
        (file) => file.scope_type === category
      );
      count += categoryFiles.length;
    });

    return count;
  }

  /**
   * Show files for a specific category
   */
  showCategoryFiles(category: 'organization' | 'global'): FileItemDTO[] {
    // Filter files based on their scope_type
    return this.filteredShareFiles.filter(
      (file) => file.scope_type === category
    );
  }

  /**
   * Toggle category expansion
   */
  toggleCategory(category: 'organization' | 'global'): void {
    this.expandedCategories[category] = !this.expandedCategories[category];
  }

  /**
   * Select file to share
   */
  selectFileToShare(file: FileItemDTO): void {
    this.fileToShare = file;
  }

  /**
   * Share selected file to current scope with event role
   */
  async shareFileToCurrentScope(): Promise<void> {
    if (
      !this.fileToShare ||
      (!this.isEventScope() && !this.isLocationScope())
    ) {
      return;
    }

    try {
      this.setLoading(true);

      // Determine scope and event role
      let eventRole: string | undefined;
      if (this.isEventScope()) {
        eventRole = this.shareTargetRole; // 'participant' or 'instructor'
      }

      // Create access record without moving the original file
      const scopeType = this.isEventScope() ? 'event' : 'location';
      const scopeId = this.isInEventContext
        ? this.detectedEventId || undefined
        : this.currentScope.id;

      // Validate scope_id before making API call
      const validScopeId = this.getScopeId(scopeType, scopeId);
      if (
        scopeType === 'event' &&
        (!validScopeId ||
          typeof validScopeId !== 'string' ||
          validScopeId.length !== 36)
      ) {
        console.error(
          '[DunaDocuments] Invalid event UUID for sharing:',
          validScopeId
        );
        this.showError('Invalid event UUID for sharing');
        return;
      }

      await this.mediaService.createFileAccess(
        this.fileToShare.obfuscated_name,
        scopeType,
        validScopeId,
        eventRole
      );

      this.showSuccess(
        `Fil delt med ${this.isEventScope() ? 'begivenheden' : 'lokationen'}`
      );
      this.hideShareFiles();
      await this.loadFolder(this.currentPath);
    } catch (error: any) {
      this.showError('Kunne ikke dele fil: ' + error.message);
    } finally {
      this.setLoading(false);
    }
  }

  /**
   * Helper: Get organization files from available share files
   */
  getOrganizationFiles(): FileItemDTO[] {
    return this.availableShareFiles.filter(
      (file) => file.scope_type === 'organization'
    );
  }

  /**
   * Helper: Get global files from available share files
   */
  getGlobalFiles(): FileItemDTO[] {
    return this.availableShareFiles.filter(
      (file) => file.scope_type === 'global'
    );
  }

  /**
   * Helper: Get organization files grouped by folder
   */
  getOrganizationFolders(): Array<{ name: string; files: FileItemDTO[] }> {
    const files = this.getOrganizationFiles();
    return this.groupFilesByFolder(files);
  }

  /**
   * Helper: Get global files grouped by folder
   */
  getGlobalFolders(): Array<{ name: string; files: FileItemDTO[] }> {
    const files = this.getGlobalFiles();
    return this.groupFilesByFolder(files);
  }

  /**
   * Helper: Group files by their parent folder
   */
  private groupFilesByFolder(
    files: FileItemDTO[]
  ): Array<{ name: string; files: FileItemDTO[] }> {
    const groups: { [key: string]: FileItemDTO[] } = {};

    files.forEach((file) => {
      const folderName = file.parent_folder || 'Root';
      if (!groups[folderName]) {
        groups[folderName] = [];
      }
      groups[folderName].push(file);
    });

    // Convert to array and sort by folder name
    return Object.keys(groups)
      .sort()
      .map((folderName) => ({
        name: folderName === 'null' || folderName === '/' ? 'Root' : folderName,
        files: groups[folderName],
      }));
  }

  /**
   * Remove file access from current scope (detach)
   */
  async removeFileAccess(file: FileItemDTO): Promise<void> {
    if (!this.isEventScope() && !this.isLocationScope()) {
      return;
    }

    try {
      this.setLoading(true);

      // Remove file access without affecting the original file
      const scopeType = this.isEventScope() ? 'event' : 'location';
      const scopeId = this.isInEventContext
        ? this.detectedEventId || undefined
        : this.currentScope.id;

      // Validate scope_id before making API call
      const validScopeId = this.getScopeId(scopeType, scopeId);
      if (
        scopeType === 'event' &&
        (!validScopeId ||
          typeof validScopeId !== 'string' ||
          validScopeId.length !== 36)
      ) {
        console.error(
          '[DunaDocuments] Invalid event UUID for removing access:',
          validScopeId
        );
        this.showError('Invalid event UUID for removing access');
        return;
      }

      await this.mediaService.removeFileAccess(
        file.obfuscated_name,
        scopeType,
        validScopeId
      );

      const itemType = file.is_folder ? 'Mappe' : 'Fil';
      this.showSuccess(
        itemType +
          ' fjernet fra ' +
          (this.isEventScope() ? 'begivenheden' : 'lokationen')
      );
      await this.loadFolder(this.currentPath);
    } catch (error: any) {
      this.showError('Kunne ikke fjerne filadgang: ' + error.message);
    } finally {
      this.setLoading(false);
    }
  }

  // ==================== Status Polling for Processing Files ====================

  /**
   * Start polling for files that are in processing status
   */
  private startPollingForProcessingFiles(): void {
    // Stop all existing polling
    this.stopAllPolling();

    // Find files that need polling (status is 'processing', 'queued', or 'uploading')
    // Note: 'behandler' is the Danish label, but the actual status value is 'processing'
    const processingFiles = this.currentFiles.filter(
      (file) =>
        file.status === 'processing' ||
        file.status === 'queued' ||
        file.status === 'uploading' ||
        file.status === 'initializing'
    );

    // Start polling for each processing file
    processingFiles.forEach((file) => {
      this.startPollingForFile(file.obfuscated_name);
    });
  }

  /**
   * Start polling for a specific file
   */
  private startPollingForFile(obfuscatedName: string): void {
    // Don't start polling if already polling this file
    if (this.statusPollingIntervals.has(obfuscatedName)) {
      return;
    }

    const interval = setInterval(async () => {
      try {
        // Check file status from backend
        const fileData = await this.mediaService.readFile(obfuscatedName);

        // Find the file in currentFiles and update its status
        const fileIndex = this.currentFiles.findIndex(
          (f) => f.obfuscated_name === obfuscatedName
        );
        if (fileIndex !== -1) {
          const currentFile = this.currentFiles[fileIndex];

          // Update status if it changed
          if (currentFile.status !== fileData.status) {

            // Update the file in the array
            this.currentFiles[fileIndex] = { ...currentFile, ...fileData };

            // Trigger change detection
            this.cdr.detectChanges();

            // If file is now ready, stop polling
            if (
              fileData.status === 'ready' ||
              fileData.status === 'completed'
            ) {
              this.stopPollingForFile(obfuscatedName);
            }

            // If file failed, stop polling
            if (fileData.status === 'failed') {
              this.stopPollingForFile(obfuscatedName);
            }
          } else {
            // Status hasn't changed, still processing
          }
        } else {
          // File not found in current list, stop polling
          this.stopPollingForFile(obfuscatedName);
        }
      } catch (error: any) {
        console.error(
          `[DunaDocuments] Error checking status for file ${obfuscatedName}:`,
          error
        );
        // Don't stop polling on error, just log it
      }
    }, this.POLL_INTERVAL);

    this.statusPollingIntervals.set(obfuscatedName, interval);
  }

  /**
   * Stop polling for a specific file
   */
  private stopPollingForFile(obfuscatedName: string): void {
    const interval = this.statusPollingIntervals.get(obfuscatedName);
    if (interval) {
      clearInterval(interval);
      this.statusPollingIntervals.delete(obfuscatedName);
    }
  }

  /**
   * Stop all polling
   */
  private stopAllPolling(): void {
    this.statusPollingIntervals.forEach((interval, obfuscatedName) => {
      clearInterval(interval);
    });
    this.statusPollingIntervals.clear();
  }

  // ==================== Upload Progress Container (Body Level) ====================

  /**
   * Create and show upload progress container at body level
   */
  private createUploadProgressContainer(): void {
    if (this.uploadProgressElement) {
      return; // Already exists
    }

    // Inject component styles into the dynamically created element
    // This ensures styles apply even though element is outside component scope
    const style = this.renderer.createElement('style');
    this.renderer.setProperty(
      style,
      'textContent',
      `
      .upload-progress-container {
        position: fixed !important;
        top: 20px !important;
        right: 20px !important;
        z-index: 99999 !important;
        min-width: 320px;
      }
      .upload-progress-card {
        background: white;
        border-radius: 12px;
        padding: 20px;
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
        border: 1px solid rgba(52, 152, 219, 0.1);
      }
      .upload-progress-header {
        display: flex;
        align-items: center;
        gap: 12px;
        margin-bottom: 16px;
      }
      .upload-progress-header .material-icons {
        color: #3498db;
        font-size: 24px;
      }
      .upload-status {
        font-size: 14px;
        font-weight: 500;
        color: #2c3e50;
      }
      .upload-progress-bar {
        width: 100%;
        height: 8px;
        background: #e0e0e0;
        border-radius: 4px;
        overflow: hidden;
        margin-bottom: 12px;
        transition: width 0.3s ease;
      }
      .upload-percentage {
        text-align: center;
        font-size: 16px;
        font-weight: 600;
        color: #3498db;
      }
    `
    );
    this.renderer.appendChild(this.document.head, style);

    const container = this.renderer.createElement('div');
    this.renderer.addClass(container, 'upload-progress-container');

    const card = this.renderer.createElement('div');
    this.renderer.addClass(card, 'upload-progress-card');

    const header = this.renderer.createElement('div');
    this.renderer.addClass(header, 'upload-progress-header');

    const icon = this.renderer.createElement('span');
    this.renderer.addClass(icon, 'material-icons');
    this.renderer.setProperty(icon, 'textContent', 'cloud_upload');

    const status = this.renderer.createElement('span');
    this.renderer.addClass(status, 'upload-status');
    this.renderer.setProperty(status, 'id', 'upload-status-text');

    this.renderer.appendChild(header, icon);
    this.renderer.appendChild(header, status);

    const progressBar = this.renderer.createElement('div');
    this.renderer.addClass(progressBar, 'upload-progress-bar');
    this.renderer.setProperty(progressBar, 'id', 'upload-progress-bar');

    const percentage = this.renderer.createElement('div');
    this.renderer.addClass(percentage, 'upload-percentage');
    this.renderer.setProperty(percentage, 'id', 'upload-percentage-text');

    this.renderer.appendChild(card, header);
    this.renderer.appendChild(card, progressBar);
    this.renderer.appendChild(card, percentage);
    this.renderer.appendChild(container, card);
    this.renderer.appendChild(this.document.body, container);

    this.uploadProgressElement = container;
  }

  /**
   * Update upload progress container
   */
  private updateUploadProgressContainer(): void {
    if (!this.uploadProgressElement) {
      return;
    }

    const statusText = this.document.getElementById('upload-status-text');
    const percentageText = this.document.getElementById(
      'upload-percentage-text'
    );
    const progressBar = this.document.getElementById('upload-progress-bar');

    if (statusText) {
      this.renderer.setProperty(statusText, 'textContent', this.uploadStatus);
    }

    if (percentageText) {
      this.renderer.setProperty(
        percentageText,
        'textContent',
        `${Math.round(this.uploadProgress)}%`
      );
    }

    if (progressBar) {
      this.renderer.setStyle(progressBar, 'width', `${this.uploadProgress}%`);
      this.renderer.setStyle(progressBar, 'height', '8px');
      this.renderer.setStyle(progressBar, 'background', '#3498db');
      this.renderer.setStyle(progressBar, 'border-radius', '4px');
      this.renderer.setStyle(progressBar, 'transition', 'width 0.3s ease');
    }
  }

  /**
   * Remove upload progress container from body
   */
  private removeUploadProgressContainer(): void {
    if (this.uploadProgressElement) {
      this.renderer.removeChild(this.document.body, this.uploadProgressElement);
      this.uploadProgressElement = null;
    }
  }

  /**
   * Clean up on component destroy
   */
  ngOnDestroy(): void {
    this.stopAllPolling();
    this.removeUploadProgressContainer();
  }
}
